import { atom } from "jotai";

import { PatronEditBooking } from "../../../types";
import { currentBookingAtom } from "../../stores";
import { normalizeGroupHosts } from "../setHostIdAndIndexOnRoundAtom/setHostIdAndIndexOnRoundAtom";
import { shouldClearRatesForPlayerSwap } from "./shouldClearRatesForPlayerSwap";

const isRateLocked = (round: PatronEditBooking.Round): boolean => {
  if (round.type === "Golf") {
    return round.roundRate?.isLocked ?? round.bookedRate?.isLocked ?? false;
  }

  return round.rate?.isLocked ?? false;
};

const sanitizeRoundRateForPlayer = (
  round: PatronEditBooking.Round,
  previousPlayer: PatronEditBooking.RoundPlayer,
  player: PatronEditBooking.RoundPlayer,
  isPrivate: boolean
) => {
  if (!shouldClearRatesForPlayerSwap(previousPlayer, player, isPrivate)) {
    return round;
  }
  if (isRateLocked(round)) return round;

  if (round.type === "Golf") {
    return { ...round, roundRate: null, bookedRate: null };
  }

  return { ...round, rate: null };
};

type Payload = {
  player: PatronEditBooking.RoundPlayer;
  roundId: string;
};

export const replacePlayerOnRoundAtom = atom(
  null,
  (get, set, payload: Payload) => {
    const booking = get(currentBookingAtom);
    const round = booking?.rounds.find(
      (round) => round.localId === payload.roundId
    );

    if (!round || !booking) return;
    if (
      payload.player.id &&
      booking.rounds.some(
        (other) =>
          other.localId !== payload.roundId &&
          other.player.id === payload.player.id
      )
    )
      return;
    const sanitizedRound = sanitizeRoundRateForPlayer(
      round,
      round.player,
      payload.player,
      booking.isPrivate ?? false
    );
    const newRound = {
      ...sanitizedRound,
      ...(payload.player.clientType === "newGuest"
        ? { hostId: payload.player.hostId || null }
        : {}),
      serverId:
        ["member", "memberHold", "customer", "unknownCustomer"].includes(
          round.player.clientType
        ) ===
        ["member", "memberHold", "customer", "unknownCustomer"].includes(
          payload.player.clientType
        )
          ? round.serverId
          : null,
      player: payload.player,
    };
    const updatedRounds = booking.rounds.map((round) =>
      round.localId === payload.roundId ? newRound : round
    );
    const rounds =
      payload.player.clientType === "newGuest" && payload.player.hostId
        ? normalizeGroupHosts(
            updatedRounds,
            booking.isPrivate &&
              booking.status === "DRAFT" &&
              updatedRounds.every((round) => round.bookingIndex == null)
          ).sort((a, b) => a.index - b.index)
        : updatedRounds;
    const newBooking = { ...booking, rounds };
    set(currentBookingAtom, newBooking);
  }
);
